import {ChangeDetectionStrategy, Component, computed, inject, input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslocoPipe} from '@jsverse/transloco';
import {UIChart} from 'primeng/chart';
import {ProviderOverallResult} from '../../../types/overall-results.types';
import {
  getChartColor,
  createScoreChartData,
} from '../../../../../../../../../shared/utils/score-chart.utils';
import {SCORE_COLORS} from '../../../../../../../../../shared/constants/provider-results.constants';
import {AppCurrencyPipe, AppNumberPipe} from '@frontend/shared';
import {RECOMMENDATION_PERCENTAGE_THRESHOLDS} from '../../../../../../../../../shared/constants/recommendation-thresholds.constants';
import {OverallResultsCostService} from '../../../services/overall-results-cost.service';
import {ScoreBarComponent} from '../../../../../../../../../shared/components/score-bar/score-bar.component';

interface PhaseScoreDisplay {
  labelKey: string;
  percentage: number;
}

@Component({
  selector: 'app-provider-card',
  imports: [
    AppNumberPipe,
    CommonModule,
    TranslocoPipe,
    UIChart,
    AppCurrencyPipe,
    ScoreBarComponent,
  ],
  templateUrl: './provider-card.component.html',
  styleUrls: ['./provider-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProviderCardComponent {
  private readonly overallResultsCostService = inject(OverallResultsCostService);
  readonly providerResult = input.required<ProviderOverallResult>();

  private readonly chartBaseOptions = {
    animation: false,
    cutout: '82%',
    rotation: 0,
    circumference: 360,
    plugins: {
      legend: {display: false},
      tooltip: {enabled: false},
      datalabels: {display: false},
    },
    interaction: {mode: undefined},
    events: [],
  };

  protected readonly overallChartOptions = {
    ...this.chartBaseOptions,
    cutout: '80%',
  };

  protected readonly overallChartData = computed(() =>
    createScoreChartData(this.providerResult().scores.overallPercentage),
  );

  protected readonly overallColor = computed(() =>
    getChartColor(this.providerResult().scores.overallPercentage),
  );

  protected readonly phaseScores = computed((): PhaseScoreDisplay[] => {
    const scores = this.providerResult().scores;
    return [
      {
        labelKey: 'project.steps.overallResults.card.rfiScore',
        percentage: scores.rfiPercentage,
      },
      {
        labelKey: 'project.steps.overallResults.card.rfpScore',
        percentage: scores.rfpPercentage,
      },
      {
        labelKey: 'project.steps.overallResults.card.rfdScore',
        percentage: scores.rfdPercentage,
      },
      {
        labelKey: 'project.steps.overallResults.card.rfqScore',
        percentage: scores.rfqPercentage,
      },
    ];
  });

  protected readonly recommendationInfo = computed(() => {
    const percentage = this.providerResult().scores.overallPercentage;
    return this.getRecommendationInfo(percentage);
  });

  protected readonly totalCost = computed(() => {
    return this.overallResultsCostService.getTotalCost(this.providerResult());
  });

  protected readonly totalCostCurrencyData = computed(() => {
    return this.overallResultsCostService.getCurrencyData(this.providerResult());
  });

  private getRecommendationInfo(percentage: number): {
    textKey: string;
    color: string;
    tooltipKey: string;
  } {
    if (percentage >= RECOMMENDATION_PERCENTAGE_THRESHOLDS.highlyRecommended) {
      return {
        textKey: 'project.steps.overallResults.recommendation.highlyRecommended',
        color: SCORE_COLORS.darkGreen,
        tooltipKey: 'project.steps.overallResults.recommendation.tooltip',
      };
    }
    if (percentage >= RECOMMENDATION_PERCENTAGE_THRESHOLDS.recommended) {
      return {
        textKey: 'project.steps.overallResults.recommendation.recommended',
        color: SCORE_COLORS.lightGreen,
        tooltipKey: 'project.steps.overallResults.recommendation.tooltip',
      };
    }
    if (percentage >= RECOMMENDATION_PERCENTAGE_THRESHOLDS.conditionallyRecommended) {
      return {
        textKey: 'project.steps.overallResults.recommendation.conditionallyRecommended',
        color: SCORE_COLORS.yellow,
        tooltipKey: 'project.steps.overallResults.recommendation.tooltip',
      };
    }
    if (percentage >= RECOMMENDATION_PERCENTAGE_THRESHOLDS.limitedFit) {
      return {
        textKey: 'project.steps.overallResults.recommendation.limitedFit',
        color: SCORE_COLORS.orange,
        tooltipKey: 'project.steps.overallResults.recommendation.tooltip',
      };
    }
    return {
      textKey: 'project.steps.overallResults.recommendation.notRecommended',
      color: SCORE_COLORS.red,
      tooltipKey: 'project.steps.overallResults.recommendation.tooltip',
    };
  }

  protected getPhaseColor(percentage: number): string {
    return getChartColor(percentage);
  }

  protected getScoreLabel(percentage: number): string {
    if (percentage >= RECOMMENDATION_PERCENTAGE_THRESHOLDS.highlyRecommended) return 'veryGood';
    if (percentage >= RECOMMENDATION_PERCENTAGE_THRESHOLDS.recommended) return 'good';
    if (percentage >= RECOMMENDATION_PERCENTAGE_THRESHOLDS.conditionallyRecommended)
      return 'acceptable';
    if (percentage >= RECOMMENDATION_PERCENTAGE_THRESHOLDS.limitedFit) return 'partiallyFulfilled';
    return 'insufficient';
  }
}
